import React, { ReactNode } from 'react'
import { Box, Paper, TextField, Typography } from '@mui/material'
import { ScrollWrapper } from '../../../../shared/components/ScrollWrapper'

type TPPCellUIProps = {
    isEditing: boolean
    draftContent: string
    onDraftChange: (value: string) => void
    onCommit: () => void
    onCancel: () => void
    onEnterEdit: () => void
    displayText: string
    hasDisplayContent: boolean
    isReadOnly?: boolean
    isMovePending?: boolean
    contentMaxHeight?: number
    backgroundColor: string
    borderColor: string
    borderWidth?: number
    textColor?: string
    placeholderColor?: string
    contentOpacity?: number
    showFooter?: boolean
    footerLeft?: ReactNode
    footerRight?: ReactNode
    topRight?: ReactNode
    onMouseEnter?: () => void
    onMouseLeave?: () => void
}

const TPPCellUI: React.FC<TPPCellUIProps> = ({
    isEditing,
    draftContent,
    onDraftChange,
    onCommit,
    onCancel,
    onEnterEdit,
    displayText,
    hasDisplayContent,
    isReadOnly,
    isMovePending,
    contentMaxHeight = 350,
    backgroundColor,
    borderColor,
    borderWidth = 2,
    textColor = 'figma.grayscale.tier2',
    placeholderColor = 'figma.grayscale.tier4',
    contentOpacity = 1,
    showFooter = true,
    footerLeft,
    footerRight,
    topRight,
    onMouseEnter,
    onMouseLeave,
}) => {
    return (
        <Paper
            elevation={0}
            sx={{
                width: '100%',
                height: '100%',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                padding: 2,
                borderRadius: 2,
                position: 'relative',
                boxSizing: 'border-box',
                border: `${borderWidth}px solid`,
                borderColor,
                transition: 'border-color 1s ease-out',
                backgroundColor,
            }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {topRight}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    maxHeight: contentMaxHeight,
                    position: 'relative',
                    paddingRight: 3,
                }}
            >
                {isEditing ? (
                    <ScrollWrapper
                        arrowColor="figma.grayscale.tier4"
                        fadeColor={backgroundColor}
                        arrowOffset={12}
                        arrowSize="10px"
                        containerSx={{ maxHeight: contentMaxHeight, width: '100%' }}
                    >
                        <TextField
                            value={draftContent}
                            onChange={(e) => onDraftChange(e.target.value)}
                            onBlur={onCommit}
                            multiline
                            minRows={3}
                            variant="standard"
                            autoFocus
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    color: textColor,
                                    fontSize: 16,
                                    fontWeight: 400,
                                    lineHeight: 1.4,
                                    alignItems: 'flex-start',
                                },
                            }}
                            sx={{
                                flex: 1,
                                width: '100%',
                                minWidth: 0,
                                '& .MuiInputBase-root': {
                                    p: 0,
                                    width: '100%',
                                },
                                '& .MuiInputBase-input': {
                                    overflow: 'visible',
                                    width: '100%',
                                },
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                    e.preventDefault()
                                    onCommit()
                                    return
                                }
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault()
                                    ;(e.target as HTMLInputElement).blur()
                                }
                            }}
                        />
                    </ScrollWrapper>
                ) : (
                    <ScrollWrapper
                        arrowColor="figma.grayscale.tier4"
                        fadeColor={backgroundColor}
                        arrowOffset={12}
                        arrowSize="10px"
                        containerSx={{ maxHeight: contentMaxHeight }}
                    >
                        <Typography
                            onClick={() => {
                                if (isReadOnly) return
                                onEnterEdit()
                            }}
                            sx={{
                                whiteSpace: 'pre-wrap',
                                minWidth: 0,
                                overflowWrap: 'anywhere',
                                color: hasDisplayContent ? textColor : placeholderColor,
                                fontSize: 16,
                                fontWeight: 400,
                                fontStyle: hasDisplayContent ? 'normal' : 'italic',
                                cursor: isReadOnly ? 'not-allowed' : 'text',
                                opacity: contentOpacity,
                            }}
                        >
                            {displayText}
                        </Typography>
                    </ScrollWrapper>
                )}
            </Box>

            {showFooter && (
                <Box
                    sx={{
                        mt: 1.5,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        minWidth: 0,
                    }}
                >
                    {footerLeft}
                    {footerRight}
                </Box>
            )}

            {isMovePending && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: 2,
                        bgcolor: 'rgba(255,255,255,0.05)',
                    }}
                />
            )}
        </Paper>
    )
}

export default TPPCellUI
